import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import StyledMenuItem from '@/components/common/styledMenuItem';
import SingleHistoricalTrend from '@/components/grids/historical/SingleHistoricalTrend';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { useHoldingTabs } from '@/hooks/useHoldingTabs';
import HoldingDiversificationChart from '@/modules/portfolio/charts/holdingDiversificationChart';
import GainLoss from '@/modules/portfolio/gainLoss/GainLoss';
import { mobileWidth } from '@/constants';
import { isLiveAsOfDate } from '@/utils/date';
import { holdingAccountLabel } from '@/utils/holdingAccountLabel';

const TAX_LOTS_TAB_ID = 'taxlots';
import {
  HoldingViewModel,
  useUpdateHoldingMutation,
} from '@/store/holdingsApi';
import { DiversificationResponse } from '@/store/diversificationsApi';
import { RecordResponse } from '@/models/ServiceTypes';
import { createExcelExportWorker } from '@/utils/excelExportWorker';
import {
  Box,
  Divider,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRowModel,
} from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import DateRange from '@/components/date/DateRange';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridContainer } from '@/components/grids/GridContainer';

interface AlsoHeldInGridProps {
  alsoHeldHoldingsData?: RecordResponse<HoldingViewModel>;
  showCusip?: boolean;
  /**
   * Whether the cost basis column may be edited. These rows carry the flyout's as-of date, but
   * the update writes to the live holding, so a dated grid is read-only.
   */
  allowCostBasisEdit?: boolean;
}

const AlsoHeldInGrid: React.FC<AlsoHeldInGridProps> = ({
  alsoHeldHoldingsData,
  showCusip,
  allowCostBasisEdit = true,
}) => {
  /** Undefined means no advisor gating context (end-user flyout): preserve
   * the legacy behavior of always showing CUSIP. Only an explicit `false`
   * (advisor, firm has "Show CUSIPs" disabled) hides the column. */
  const shouldShowCusipColumn = showCusip !== false;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [updateHolding] = useUpdateHoldingMutation();
  const { t } = useLocalization();
  const isNative = Capacitor.getPlatform() !== 'web';

  // Match HistoricalGrid styling
  const gridStyles = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: isDarkMode
        ? 'rgba(255, 255, 255, 0.08)'
        : theme.palette.grey[100],
      borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
      borderBottom: 'none',
    },
    '& .MuiDataGrid-cellContent': {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
  };

  // Match HistoricalGrid value color logic
  const getValueColor = (value: number) => {
    if (value > 0) return 'success.main';
    if (value < 0) return 'error.main';
    return 'text.primary';
  };

  // Match HistoricalGrid column creation patterns
  const createBaseColumnProps = () => ({
    headerAlign: 'right' as const,
    align: 'right' as const,
  });

  const { tickerUrlFormatter } = useTickerUrlFormatter();

  const rows = useMemo(() => {
    if (!alsoHeldHoldingsData) return [];
    return alsoHeldHoldingsData.Records.map((h) => ({
      id: h.HoldingId,
      ...h,
    }));
  }, [alsoHeldHoldingsData]);

  // Handle row update for cost basis editing
  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      if (!allowCostBasisEdit) {
        return oldRow;
      }

      try {
        // Check if Cost Basis was actually changed
        if (newRow.CostBasis !== oldRow.CostBasis) {
          const costBasis = parseFloat(newRow.CostBasis as string);

          // Validate the input
          if (isNaN(costBasis) || costBasis < 0) {
            throw new Error('Invalid cost basis value');
          }

          // Call the update API
          await updateHolding({
            HoldingId: newRow.HoldingId as number,
            CostBasis: costBasis,
          }).unwrap();
        }

        return newRow;
      } catch (error) {
        console.error('Failed to update cost basis:', error);
        // Return the old row to revert the change
        return oldRow;
      }
    },
    [updateHolding, allowCostBasisEdit],
  );

  const columns: GridColDef[] = [
    {
      field: 'AccountName',
      headerName: t('grid.account'),
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row: HoldingViewModel) => holdingAccountLabel(row),
      renderCell: ({ value }) => (
        <Typography
          variant='body2'
          sx={{ fontWeight: 500, width: '100%' }}
        >
          {value}
        </Typography>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'Symbol',
      headerName: t('grid.ticker'),
      width: 100,
      renderCell: ({ value }) => {
        if (!value) {
          return (
            <Typography
              variant='body2'
              sx={{
                fontFamily: 'monospace',
                color: 'text.secondary',
                width: '100%',
                textAlign: 'right',
              }}
            >
              --
            </Typography>
          );
        }
        return (
          <Box sx={{ width: '100%', textAlign: 'right' }}>
            {tickerUrlFormatter(value)}
          </Box>
        );
      },
    },
    {
      ...createBaseColumnProps(),
      field: 'CUSIP',
      headerName: 'CUSIP',
      width: 110,
      renderCell: ({ value }) => (
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            width: '100%',
            textAlign: 'right',
          }}
        >
          {value || '--'}
        </Typography>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'Quantity',
      headerName: t('grid.quantity'),
      width: 120,
      valueFormatter: (value: number | null | undefined) =>
        value == null || isNaN(value)
          ? '--'
          : new Intl.NumberFormat('en-US', {
              maximumFractionDigits: 4,
            }).format(value),
    },
    {
      ...createBaseColumnProps(),
      field: 'Price',
      headerName: t('grid.price'),
      width: 110,
      valueFormatter: (value: number | null | undefined) =>
        value == null || isNaN(value)
          ? '--'
          : new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value),
    },
    {
      ...createBaseColumnProps(),
      field: 'Balance',
      headerName: t('grid.balance'),
      width: 140,
      renderCell: ({ value }) => (
        <Typography
          variant='body2'
          sx={{
            color: getValueColor(Number(value)),
            fontWeight: 500,
            width: '100%',
            textAlign: 'right',
          }}
        >
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(Number(value))}
        </Typography>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'CostBasis',
      headerName: t('grid.costBasis'),
      width: 130,
      editable: allowCostBasisEdit,
      type: 'string',
      valueFormatter: (value: number | null | undefined) =>
        value == null || isNaN(value)
          ? '--'
          : new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value),
      valueParser: (value: string | number | null | undefined) => {
        if (value == null) return null;

        const stringValue =
          typeof value === 'string' ? value : value.toString();

        if (!stringValue || stringValue.trim() === '') return null;

        const parsed = parseFloat(stringValue);
        return isNaN(parsed) ? null : Math.round(parsed * 100) / 100; // Round to 2 decimal places
      },
      renderEditCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            pl: 1,
          }}
        >
          <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
          <input
            type='text'
            defaultValue={
              (params.value as number | null | undefined)?.toFixed(2) ?? '0.00'
            }
            autoFocus
            onBlur={(e) => {
              const value = e.target.value;
              if (value && !isNaN(parseFloat(value))) {
                const formatted = parseFloat(value).toFixed(2);
                void params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: parseFloat(formatted),
                });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value;
                if (value && !isNaN(parseFloat(value))) {
                  const formatted = parseFloat(value).toFixed(2);
                  void params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: parseFloat(formatted),
                  });
                }
                params.api.stopCellEditMode({
                  id: params.id,
                  field: params.field,
                });
              } else if (e.key === 'Escape') {
                params.api.stopCellEditMode({
                  id: params.id,
                  field: params.field,
                });
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              const regex = /^\d*\.?\d{0,2}$/;
              if (value !== '' && !regex.test(value)) {
                e.preventDefault();
              }
            }}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              textAlign: 'right',
              padding: '4px 8px',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              backgroundColor: 'transparent',
            }}
          />
        </Box>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'UpdatedDate',
      headerName: t('grid.lastUpdated'),
      width: 130,
      valueFormatter: (value: string | null | undefined) =>
        value ? dayjs(value).format('M/D/YYYY') : '--',
    },
  ];

  const visibleColumns = columns.filter(
    (column) => shouldShowCusipColumn || column.field !== 'CUSIP',
  );

  if (!alsoHeldHoldingsData || alsoHeldHoldingsData.Records.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color='text.secondary'>
          This holding is not found in any other accounts.
        </Typography>
      </Box>
    );
  }

  return (
    <PaperFrameStyled sx={{ px: 3, pb: 3 }}>
      <Box sx={{ px: 1, mt: 3, width: '100%' }}>
        <GridContainer
          isWidget={false}
          size='flyout'
        >
          <DataGridPremium
            rows={rows}
            columns={visibleColumns}
            disableRowSelectionOnClick
            showToolbar
            disablePivoting
            processRowUpdate={processRowUpdate}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                excelOptions: { worker: createExcelExportWorker },
              },
            }}
            slots={{
              columnMenu: () => null,
              ...(isNative
                ? {
                    toolbar: (props) => (
                      <CustomToolbarForNative
                        {...props}
                        excelWorker={createExcelExportWorker}
                      />
                    ),
                  }
                : {}),
            }}
            disableColumnMenu
            sx={gridStyles}
          />
        </GridContainer>
      </Box>
    </PaperFrameStyled>
  );
};

interface HoldingTabsProps {
  holding: HoldingViewModel;
  diversificationData?: DiversificationResponse;
  isDiversificationLoading?: boolean;
  diversificationError?: unknown;
  endDate?: string;
  alsoHeldHoldingsData?: RecordResponse<HoldingViewModel>;
  investorId?: number;
  showCusip?: boolean;
}

const HoldingTabs: React.FC<HoldingTabsProps> = ({
  holding,
  diversificationData,
  isDiversificationLoading,
  diversificationError,
  endDate,
  alsoHeldHoldingsData,
  investorId,
  showCusip,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobileMatch = useMediaQuery(mobileWidth);
  const isBelow1000px = useMediaQuery(theme.breakpoints.down(1001));

  const defaultDateRange = useMemo(() => {
    const range = getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);
    return {
      startDate: dayjs(range.startDate).format('MM/DD/YYYY'),
      endDate: dayjs(range.endDate).format('MM/DD/YYYY'),
    };
  }, []);

  const [historicalStartDate, setHistoricalStartDate] = useState(
    defaultDateRange.startDate,
  );
  const [historicalEndDate, setHistoricalEndDate] = useState(
    defaultDateRange.endDate,
  );

  const handleHistoricalDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      setHistoricalStartDate(startDate);
      setHistoricalEndDate(endDate);
    },
    [],
  );

  // Use the custom hook to get tabs configuration from API
  const { tabs } = useHoldingTabs(holding, alsoHeldHoldingsData);

  /**
   * Gains and losses reads current tax lots and takes no as-of date, so on a past date it would
   * put today's quantities, prices and gains beside a historical balance. The API withholds the
   * tab from a snapshot; the flyout does not depend on that.
   */
  const availableTabs = useMemo(
    () =>
      isLiveAsOfDate(endDate)
        ? tabs
        : tabs.filter((tab) => tab.id !== TAX_LOTS_TAB_ID),
    [tabs, endDate],
  );

  const [activeTab, setActiveTab] = useState(availableTabs[0]?.id || 'pd');

  // Update activeTab if available tabs change
  useEffect(() => {
    if (!availableTabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(availableTabs[0]?.id || 'pd');
    }
  }, [availableTabs, activeTab]);

  const handleTabSelectChange = useCallback((event: SelectChangeEvent) => {
    setActiveTab(event.target.value);
  }, []);

  return (
    <Stack
      direction='column'
      gap={3}
    >
      <Divider />

      {/* Tab Navigation - Show as dropdown on mobile, inline tabs on desktop */}
      <Stack
        direction={isBelow1000px ? 'column' : 'row'}
        justifyContent='space-between'
        alignItems={isBelow1000px ? 'stretch' : 'center'}
        gap={isBelow1000px ? 2 : 0}
      >
        {isMobileMatch ? (
          <FormControl
            fullWidth
            size='small'
          >
            <Select
              labelId='holding-tabs-select-label'
              id='holding-tabs-select'
              value={activeTab}
              onChange={handleTabSelectChange}
              sx={(theme) => ({
                backgroundColor: theme.palette.custom.overlay.selected,
                color: theme.palette.primary.main,
                '& .MuiSelect-select': {
                  color: theme.palette.primary.main,
                  padding: '30px 14px',
                  fontWeight: 600,
                },
                '& .MuiSelect-icon': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                  display: 'none',
                },
                '& .MuiOutlinedInput-root': {
                  border: 'none',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                  display: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                  display: 'none',
                },
              })}
            >
              {availableTabs.map((tab) => (
                <MenuItem
                  key={tab.id}
                  value={tab.id}
                >
                  {tab.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Stack direction='row'>
            {availableTabs.map((tab) => (
              <StyledMenuItem
                key={tab.id}
                selected={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                }}
                sx={{
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: isDarkMode ? 'white' : 'rgba(0,0,0,60)',
                }}
              >
                {tab.title}
              </StyledMenuItem>
            ))}
          </Stack>
        )}

        {activeTab === 'historical' && (
          <Box
            display='flex'
            justifyContent={isBelow1000px ? 'stretch' : 'flex-end'}
            sx={{
              width: isBelow1000px ? '100%' : 'auto',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRange
                startDate={historicalStartDate}
                endDate={historicalEndDate}
                onChange={handleHistoricalDateRangeChange}
                useMobilePrimaryIconButton
              />
            </LocalizationProvider>
          </Box>
        )}
      </Stack>

      {/* Tab Content */}
      <Stack
        direction='column'
        gap={2}
      >
        <TabContent
          activeTab={activeTab}
          holding={holding}
          historicalStartDate={historicalStartDate}
          historicalEndDate={historicalEndDate}
          endDate={endDate}
          diversificationData={diversificationData}
          isDiversificationLoading={isDiversificationLoading}
          diversificationError={diversificationError}
          alsoHeldHoldingsData={alsoHeldHoldingsData}
          investorId={investorId}
          showCusip={showCusip}
        />
      </Stack>
    </Stack>
  );
};

interface TabContentProps {
  activeTab: string;
  holding: HoldingViewModel;
  historicalStartDate: string;
  historicalEndDate: string;
  endDate?: string;
  diversificationData?: DiversificationResponse;
  isDiversificationLoading?: boolean;
  diversificationError?: unknown;
  alsoHeldHoldingsData?: RecordResponse<HoldingViewModel>;
  investorId?: number;
  showCusip?: boolean;
}

const TabContent: React.FC<TabContentProps> = React.memo(
  ({
    activeTab,
    holding,
    historicalStartDate,
    historicalEndDate,
    endDate,
    diversificationData,
    isDiversificationLoading,
    diversificationError,
    alsoHeldHoldingsData,
    investorId,
    showCusip,
  }) => {
    switch (activeTab) {
      case 'historical': {
        return (
          <SingleHistoricalTrend
            holdingId={holding.HoldingId}
            startDate={historicalStartDate}
            endDate={historicalEndDate}
            asOfDate={endDate}
            investorId={investorId}
            size='flyout'
          />
        );
      }
      case 'pd': {
        return (
          <HoldingDiversificationChart
            holding={holding}
            diversificationData={diversificationData}
            asOfDate={endDate}
            isLoading={isDiversificationLoading}
            hasError={!!diversificationError}
          />
        );
      }
      case TAX_LOTS_TAB_ID: {
        return (
          <GainLoss
            isWidget
            holdingId={holding.HoldingId}
            investorId={investorId}
          />
        );
      }
      case 'other': {
        return (
          <AlsoHeldInGrid
            alsoHeldHoldingsData={alsoHeldHoldingsData}
            showCusip={showCusip}
            allowCostBasisEdit={isLiveAsOfDate(endDate)}
          />
        );
      }
      default:
        return null;
    }
  },
);

TabContent.displayName = 'TabContent';

export default React.memo(HoldingTabs);
